import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, vModelSelect as _vModelSelect, withDirectives as _withDirectives, normalizeClass as _normalizeClass, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "container-fluid" }
const _hoisted_2 = { class: "row starter-main mt-5 pt-5" }
const _hoisted_3 = { class: "d-flex justify-content-end align-items-center gap-3 mt-3 mb-4 px-3" }
const _hoisted_4 = { class: "d-flex align-items-center gap-2" }
const _hoisted_5 = ["value"]
const _hoisted_6 = { class: "pagination-mini" }
const _hoisted_7 = ["disabled"]
const _hoisted_8 = ["disabled", "onClick"]
const _hoisted_9 = ["disabled"]
const _hoisted_10 = { class: "mb-3" }
const _hoisted_11 = { class: "mb-3" }

import { ref, computed, onMounted, watch } from "vue";
import { useCrud } from "@/composables/commen/useCrud";
import BaseModal from "@/components/commen/modal/BaseModal.vue";
import BaseForm, { FormField } from "@/components/commen/modal/BaseForm.vue";
import CommonTable2 from "@/components/commen/table/CommonTable2.vue"; 
import { usePermissions } from "@/composables/usePermissions";
import TableTitle from "@/components/commen/table/TableTitle.vue";
import apiService from "@/services/apiService";
import { toast, type Content } from "vue3-toastify";
import VueMultiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.css";

const API_RESOURCE = "cms-agent-meta/competencies";
const pageTitle = "Competency";
const title = "Agent Competencies";


export default /*@__PURE__*/_defineComponent({
  __name: 'AgentCompetencies',
  setup(__props) {

const { canCreate } = usePermissions();
const crud = useCrud(API_RESOURCE);

const tablePagination = ref({
  current_page: 1,
  per_page: 10,
  total: 0,
  total_pages: 0,
  has_next: false,
  has_prev: false,
});

const currentPage = ref(1);

const visiblePages = computed(() => {
  const total = tablePagination.value.total_pages;
  const current = tablePagination.value.current_page;
  if (!total || total <= 4) return Array.from({ length: Math.max(1, total) }, (_, i) => i + 1);
  const pages: (number | string)[] = [1];
  if (current > 4) pages.push("...");
  const windowStart = Math.max(2, current - 1);
  const windowEnd = Math.min(total - 1, current + 1);
  for (let i = windowStart; i <= windowEnd; i++) pages.push(i);
  if (current < total - 3) pages.push("...");
  pages.push(total);
  return pages;
});

const rows = ref<any[]>([]);
const tableConfig = ref({
  columns: [
    { name: "Title", field: "title", sortable: true, width: "200px" },
    { name: "Description", field: "description", sortable: true, width: "250px" },
    { name: "Knowledge Bases", field: "kb_count", sortable: true,  type: "badge" }
  ],
  data: rows,
  actions: [true],
  loading: crud.isLoading,
  pagination: tablePagination,
  showCategoryFilter: false,
  showStatusFilter: false,
  table_width: "max-content",  
});

watch(crud.data, (newVal) => {
  rows.value = (newVal || []).map((item: any) => ({
    ...item,
    kb_count: (item.knowledge_base_ids || []).length
  }));
});
watch(crud.isLoading, (v) => tableConfig.value.loading = v);
watch(() => crud.pagination.value, (newVal) => {
  if (!newVal) return;
  tablePagination.value.current_page = newVal.current_page || 1;
  tablePagination.value.total_pages = newVal.total_pages || 1;
  tablePagination.value.total = newVal.total || 0;
  tablePagination.value.has_prev = tablePagination.value.current_page > 1;
  tablePagination.value.has_next = tablePagination.value.current_page < tablePagination.value.total_pages;
}, { immediate: true, deep: true });

const changeRows = () => {
  currentPage.value = 1;
  crud.fetchData({ page: 1, limit: tablePagination.value.per_page });
};
const handlePageChange = (page: number) => {
  if (page < 1 || page === tablePagination.value.current_page) return;
  if (tablePagination.value.total_pages && page > tablePagination.value.total_pages) return;
  currentPage.value = page;
  crud.fetchData({ page, limit: tablePagination.value.per_page });
};

const isCreateModalOpen = ref(false);
const isEditModalOpen = ref(false);
const isViewMode = ref(false);
const newItemData = ref({ title: "", description: "", knowledge_base_ids: [] });
const editingItemData = ref<any>({ _id: "", title: "", description: "", knowledge_base_ids: [] });

const knowledgeBases = ref<any[]>([]);

const loadKnowledgeBases = async () => {
  try {
    const response = await apiService.getKnowledgeBases({ isActive: true });
    if (response) {
      knowledgeBases.value = response.data || response.knowledgeBases || [];
    }
  } catch (err: any) {
    console.error("Error loading knowledge bases:", err);
  }
};

const createFormFields: FormField[] = [
  { key: "title", label: "Title", type: "text", required: true, placeholder: "Enter title" },
  { key: "description", label: "Description", type: "textarea", placeholder: "Enter description" },
];
const editFormFields = computed<FormField[]>(() => [
  { key: "title", label: "Title", type: "text", required: true, placeholder: "Enter title", readonly: isViewMode.value },
  { key: "description", label: "Description", type: "textarea", placeholder: "Enter description", readonly: isViewMode.value },
]);

const isCreateFormValid = computed(() => !newItemData.value.title.trim());
const isEditFormValid = computed(() => !editingItemData.value.title?.trim());

const handleEdit = (row: any) => {
  isViewMode.value = false;
  editingItemData.value = { 
    ...row,
    knowledge_base_ids: (row.knowledge_base_ids || []).map((kb: any) => {
      const id = typeof kb === 'object' ? (kb._id || kb.id) : kb;
      const found = knowledgeBases.value.find(k => (k._id || k.id) === id);
      return found || kb;
    })
  };
  isEditModalOpen.value = true;
};
const handleView = (row: any) => {
  isViewMode.value = true;
  editingItemData.value = { 
    ...row,
    knowledge_base_ids: (row.knowledge_base_ids || []).map((kb: any) => {
      const id = typeof kb === 'object' ? (kb._id || kb.id) : kb;
      const found = knowledgeBases.value.find(k => (k._id || k.id) === id);
      return found || kb;
    })
  };
  isEditModalOpen.value = true;
};
const handleDelete = async (id: string) => {
  if (confirm("Are you sure?")) {
    await crud.deleteItem(id);
    await crud.fetchData({ page: tablePagination.value.current_page, limit: tablePagination.value.per_page });
  }
};
const handleSaveCreate = async () => {
  if (isCreateFormValid.value) return;
  const payload = {
    ...newItemData.value,
    knowledge_base_ids: newItemData.value.knowledge_base_ids.map((kb: any) => typeof kb === 'object' ? (kb._id || kb.id) : kb)
  };
  const res = await crud.createItem(payload);
  if (res) {
    isCreateModalOpen.value = false;
    newItemData.value = { title: "", description: "", knowledge_base_ids: [] };
    await crud.fetchData({ page: 1, limit: tablePagination.value.per_page });
  }
};
const handleSaveEdit = async () => {
  const id = editingItemData.value._id;
  if (!id) return;
  const { _id, ...rest } = editingItemData.value;
  const payload = {
    ...rest,
    knowledge_base_ids: rest.knowledge_base_ids.map((kb: any) => typeof kb === 'object' ? (kb._id || kb.id) : kb)
  };
  try {
    crud.isLoading.value = true;
    const res = await apiService.put(API_RESOURCE, id, payload);
    if (res) {
      toast.success("Item updated successfully" as Content, { theme: "colored" });
      isEditModalOpen.value = false;
      await crud.fetchData({ page: tablePagination.value.current_page, limit: tablePagination.value.per_page });
    }
  } catch (err: any) {
    toast.error((err.response?.data?.message || err.message || "Failed to update.") as Content, { theme: "colored" });
  } finally {
    crud.isLoading.value = false;
  }
};
const closeCreateModal = () => { isCreateModalOpen.value = false; newItemData.value = { title: "", description: "", knowledge_base_ids: [] }; };
const closeEditModal = () => { isEditModalOpen.value = false; isViewMode.value = false; editingItemData.value = { _id: "", title: "", description: "", knowledge_base_ids: [] }; };

onMounted(() => {
  loadKnowledgeBases();
  crud.fetchData({ page: tablePagination.value.current_page, limit: tablePagination.value.per_page });
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _createVNode(TableTitle, {
          title: title,
          pageTitle: "Competencies",
          icon: "fas fa-certificate",
          showAction: true,
          actionLabel: "Create New",
          modal: "createNew",
          disabled: !_unref(canCreate)('agents')
        }, null, 8, ["disabled"]),
        _createVNode(CommonTable2, {
          tableObj: tableConfig.value,
          onEdit: handleEdit,
          onDelete: handleDelete,
          onView: handleView,
          permissionKey: "agents"
        }, null, 8, ["tableObj"])
      ])
    ]),
    _createElementVNode("div", _hoisted_3, [
      _createElementVNode("div", _hoisted_4, [
        _cache[7] || (_cache[7] = _createElementVNode("span", { class: "text-muted" }, "Rows per page:", -1)),
        _withDirectives(_createElementVNode("select", {
          class: "form-select form-select-sm rows-select",
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((tablePagination.value.per_page) = $event)),
          onChange: changeRows
        }, [
          (_openBlock(), _createElementBlock(_Fragment, null, _renderList([5, 10, 20, 50, 100], (n) => {
            return _createElementVNode("option", {
              key: n,
              value: n
            }, _toDisplayString(n), 9, _hoisted_5)
          }), 64))
        ], 544), [
          [_vModelSelect, tablePagination.value.per_page]
        ])
      ]),
      _createElementVNode("div", _hoisted_6, [
        _createElementVNode("button", {
          class: "page-btn prev_btn",
          disabled: !tablePagination.value.has_prev,
          onClick: _cache[1] || (_cache[1] = ($event: any) => (handlePageChange(tablePagination.value.current_page - 1)))
        }, " « ", 8, _hoisted_7),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visiblePages.value, (p, index) => {
          return (_openBlock(), _createElementBlock("button", {
            key: index,
            class: _normalizeClass(["page-btn", {
          'page-active': p === tablePagination.value.current_page,
          'disabled-dots': p === '...',
        }]),
            disabled: p === '...',
            onClick: ($event: any) => (p !== '...' && typeof p === 'number' && handlePageChange(p))
          }, _toDisplayString(p), 11, _hoisted_8))
        }), 128)),
        _createElementVNode("button", {
          class: "page-btn next_btn",
          disabled: !tablePagination.value.has_next,
          onClick: _cache[2] || (_cache[2] = ($event: any) => (handlePageChange(tablePagination.value.current_page + 1)))
        }, " » ", 8, _hoisted_9)
      ])
    ]),
    _createVNode(BaseModal, {
      id: "createNew",
      title: `Create ${pageTitle}`,
      visible: isCreateModalOpen.value,
      onClose: closeCreateModal,
      onSave: handleSaveCreate,
      "save-button-disabled": isCreateFormValid.value,
      saveButtonText: "Create"
    }, {
      body: _withCtx(() => [
        _createVNode(BaseForm, {
          fields: createFormFields,
          modelValue: newItemData.value,
          "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((newItemData).value = $event)),
          onSubmit: handleSaveCreate,
          ref: "createFormRef"
        }, {
          "additional-fields": _withCtx(() => [
            _createElementVNode("div", _hoisted_10, [
              _cache[8] || (_cache[8] = _createElementVNode("label", { class: "form-label" }, "Knowledge Bases", -1)),
              _createVNode(_unref(VueMultiselect), {
                modelValue: newItemData.value.knowledge_base_ids,
                "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((newItemData.value.knowledge_base_ids) = $event)),
                options: knowledgeBases.value,
                multiple: true,
                "close-on-select": false,
                "clear-on-select": false,
                "preserve-search": true,
                placeholder: "Select Knowledge Bases",
                label: "name",
                "track-by": "_id",
                "preselect-first": false
              }, null, 8, ["modelValue", "options"])
            ])
          ]),
          _: 1
        }, 8, ["modelValue"])
      ]),
      _: 1
    }, 8, ["title", "visible", "save-button-disabled"]),
    _createVNode(BaseModal, {
      id: "editModal",
      title: `${isViewMode.value ? 'View' : 'Edit'} ${pageTitle}`,
      visible: isEditModalOpen.value,
      onClose: closeEditModal,
      onSave: handleSaveEdit,
      "show-save-button": !isViewMode.value,
      "save-button-disabled": isEditFormValid.value
    }, {
      body: _withCtx(() => [
        _createVNode(BaseForm, {
          fields: editFormFields.value,
          modelValue: editingItemData.value,
          "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((editingItemData).value = $event)),
          onSubmit: handleSaveEdit,
          ref: "editFormRef"
        }, {
          "additional-fields": _withCtx(() => [
            _createElementVNode("div", _hoisted_11, [
              _cache[9] || (_cache[9] = _createElementVNode("label", { class: "form-label" }, "Knowledge Bases", -1)),
              _createVNode(_unref(VueMultiselect), {
                modelValue: editingItemData.value.knowledge_base_ids,
                "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((editingItemData.value.knowledge_base_ids) = $event)),
                options: knowledgeBases.value,
                multiple: true,
                "close-on-select": false,
                "clear-on-select": false,
                "preserve-search": true,
                placeholder: "Select Knowledge Bases",
                label: "name",
                "track-by": "_id",
                "preselect-first": false,
                disabled: isViewMode.value
              }, null, 8, ["modelValue", "options", "disabled"])
            ])
          ]),
          _: 1
        }, 8, ["fields", "modelValue"])
      ]),
      _: 1
    }, 8, ["title", "visible", "show-save-button", "save-button-disabled"])
  ], 64))
}
}

})